import { useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';

import { CompanyBankAccountsDocument } from 'common/hooks/useCompanyBankAccounts/graphql/companyBankAccounts.gql';

import { TaxForecastPeriodRevenueDocument } from '../graphql/taxForecastPeriodRevenue.gql';
import { TransactionSumPartialFilters } from './useTaxForecastPeriodRevenueQueryFilters';

interface UseTaxForecastRevenueOptions {
  companyProfileId: string;
  onCompleted?: ({ transactionsSum }: { transactionsSum: number }) => void;
  filters: TransactionSumPartialFilters;
}

export function useTaxForecastRevenue({
  companyProfileId,
  filters,
  onCompleted,
}: UseTaxForecastRevenueOptions): { loading: boolean; revenue: null | number } {
  const [revenue, setRevenue] = useState<number | null>(null);

  const [fetchRevenue, { loading: revenueLoading }] = useLazyQuery(
    TaxForecastPeriodRevenueDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  // Recover all bank accounts and sum all revenues
  const { loading: loadingBankAccounts } = useQuery(
    CompanyBankAccountsDocument,
    {
      fetchPolicy: 'cache-first',
      onCompleted: async ({ viewer }) => {
        const { bankAccounts } = viewer.company;

        const bankAccountIds = bankAccounts.map((b) => b.bankAccountId);
        const results = await Promise.all(
          bankAccountIds.map((bankAccountId) => {
            return fetchRevenue({
              variables: {
                filters: {
                  ...filters,
                  bankAccountId,
                },
              },
            });
          }),
        );

        const totalRevenue = results.reduce((sum, { data }) => {
          if (!data) {
            // TODO: handle error case
            return sum;
          }

          const { transactionsSum } = data;
          return sum + transactionsSum;
        }, 0);

        if (onCompleted) {
          onCompleted({ transactionsSum: totalRevenue });
        }

        setRevenue(totalRevenue);
      },
      variables: {
        companyProfileId,
      },
    },
  );

  return { loading: revenueLoading || loadingBankAccounts, revenue };
}
